import * as _ from 'underscore';

import { QueryString } from '../../common/react/QueryString';

export interface SwitchableContentScope extends ng.IScope {
  contentSwitchProps?: ContentSwitchProps;
}

export interface ContentSwitchProps {
  selectedChoiceName: string;
  contentChoices: ContentSwitchChoice[];
}

export interface ContentSwitchChoice {
  name: string;
  templateUrl: string;
  controller: Function;
}


/**
 * This controller provides a way to have a div in the page whose contents are linked to a query
 * parameter in the URL. To use it, set up a div that contains the SwitchableContentWithUrlParamCtrl.Template
 * and is controlled by a SwitchableContentWithUrlParamCtrl initialized with the desired content
 * choices and query string key.
 *
 * Each content choice consists of a name (which can be used as a loc key in the UI), a reference to
 * its template, and its corresponding controller. The scope in that controller should extend
 * SwitchableContentScope to give it access to the content choices, enabling the UI widget that
 * switches the content to exist inside the switched-out div itself.
 *
 * The UI widget that switches the content (e.g. menu, radio buttons) can be anything with an
 * ng-model. Hook up the ng-model to the "contentSwitchProps.selectedChoiceName" on the scope, which is
 * watched by this controller to update the view.
 *
 * @param choices The content choices that this controller can switch over
 * @param urlParamKey The key in the URL query string that controls the switching
 * @returns a controller class
 */
export function SwitchableContentWithUrlParamCtrl(choices: ContentSwitchChoice[], urlParamKey: string): any {
  return class {
    public static $inject = ['$scope', '$location'];

    constructor($scope: SwitchableContentScope,
                $location: ng.ILocationService) {
      const queryString: QueryString = $location.search.bind($location);

      $scope.contentSwitchProps = {
        selectedChoiceName: this.getCurrentSelectedChoice(queryString, choices).name,
        contentChoices: choices
      };

      $scope.$watch('contentSwitchProps.selectedChoiceName', (newValue: string, oldValue: string) => {
        if (oldValue !== newValue) { // prevent URL param update on initial load
          this.updateQueryStringWithSelection(queryString, newValue);
        }
      });
    }

    private getCurrentSelectedChoice(queryString: QueryString, choices: ContentSwitchChoice[]): ContentSwitchChoice {
      const nameFromUrl = queryString()[urlParamKey];
      const choiceNames = choices.map(c => c.name);
      const foundIndex = _.indexOf(choiceNames, nameFromUrl);
      return choices[foundIndex >= 0 ? foundIndex : 0];
    }

    private updateQueryStringWithSelection(queryString: QueryString, selectedChoiceName: string) {
      queryString(urlParamKey, selectedChoiceName);
    }
  };
}

export module SwitchableContentWithUrlParamCtrl {
  export const Template =
    '<div ng-repeat="choice in contentSwitchProps.contentChoices">' +
      '<div ng-if="contentSwitchProps.selectedChoiceName === choice.name" ' +
           'ng-include="choice.templateUrl" ng-controller="choice.controller"></div>' +
    '</div>';
}
